import React, { useEffect, useState } from "react";
import { FileText, Download, Eye, X, Maximize2, Minimize2 } from "lucide-react";
import api from "../api/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://srvjvss.pythonanywhere.com";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    api
      .get("reports/")
      .then((res) => {
        setReports(res.data.results || res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Reports API Error:", err);
        setError("Failed to load reports.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedFile ? "hidden" : "auto";
  }, [selectedFile]);

  const closeModal = () => {
    setSelectedFile(null);
    setIsFullscreen(false);
  };

  const annualReports = reports.filter((r) => r.report_type === "annual");
  const auditReports = reports.filter((r) => r.report_type === "audit");

  const renderReportCard = (report) => {
    const fileUrl = report.file.startsWith("http")
      ? report.file
      : `${BACKEND_URL}${report.file}`;

    const downloadUrl = `${BACKEND_URL}/api/reports/download/${report.id}/`;

    const isHovered = hoveredCard === report.id;

    return (
      <div
        key={report.id}
        style={{
          ...styles.card,
          transform: isHovered ? "translateY(-8px)" : "translateY(0px)",
          boxShadow: isHovered
            ? "0 20px 40px rgba(0,0,0,0.15)"
            : "0 4px 12px rgba(0,0,0,0.08)",
        }}
        onMouseEnter={() => setHoveredCard(report.id)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <div style={styles.header}>
          <FileText size={28} color="#d32f2f" />
          <span style={styles.badge}>PDF</span>
        </div>

        <h3 style={styles.title}>{report.title}</h3>
        <p style={styles.meta}>
          PDF • {report.year || "N/A"} • Official Document
        </p>

        {report.description && (
          <p style={styles.desc}>{report.description}</p>
        )}

        <div style={styles.buttons}>
          <button
            onClick={() => setSelectedFile(fileUrl)}
            style={{
              ...styles.viewBtn,
              transform: isHovered ? "scale(1.05)" : "scale(1)",
            }}
          >
            <Eye size={16} style={styles.iconSpace} />
            View
          </button>

          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...styles.downloadBtn,
              transform: isHovered ? "scale(1.05)" : "scale(1)",
            }}
          >
            <Download size={16} style={styles.iconSpace} />
            Download
          </a>
        </div>
      </div>
    );
  };

  if (loading) return <p style={styles.centerText}>Loading reports...</p>;
  if (error)
    return <p style={{ ...styles.centerText, color: "red" }}>{error}</p>;
  if (!reports.length)
    return <p style={styles.centerText}>No reports available</p>;

  return (
    <div style={{ padding: "40px 20px" }}>
      <h1 style={styles.heading}>Reports</h1>
      <p style={styles.trustText}>
        We believe in complete transparency. All our annual reports and
        financial statements are publicly accessible.
      </p>

      <h2 style={styles.sectionHeading}>Annual Reports</h2>
      <div style={styles.grid}>
        {annualReports.length > 0
          ? annualReports.map(renderReportCard)
          : <p style={styles.centerText}>No Annual Reports Available</p>}
      </div>

      <h2 style={styles.sectionHeading}>Audit Reports</h2>
      <div style={styles.grid}>
        {auditReports.length > 0
          ? auditReports.map(renderReportCard)
          : <p style={styles.centerText}>No Audit Reports Available</p>}
      </div>

      {selectedFile && (
        <div style={styles.modalOverlay}>
          <div
            style={
              isFullscreen
                ? styles.modalContentFullscreen
                : styles.modalContent
            }
          >
            <div style={styles.modalHeader}>
              <span style={{ fontWeight: 700 }}>PDF Preview</span>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  style={styles.iconBtn}
                >
                  {isFullscreen ? (
                    <Minimize2 size={18} />
                  ) : (
                    <Maximize2 size={18} />
                  )}
                </button>
                <button onClick={closeModal} style={styles.iconBtn}>
                  <X size={20} />
                </button>
              </div>
            </div>
            <iframe
              src={selectedFile}
              title="PDF Preview"
              style={styles.iframe}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  centerText: { textAlign: "center", fontWeight: "700", color: "#000" },
  heading: {
    marginBottom: "10px",
    textAlign: "center",
    fontWeight: "800",
    fontSize: "32px",
  },
  trustText: {
    textAlign: "center",
    maxWidth: "700px",
    margin: "0 auto 30px",
    fontSize: "16px",
    fontWeight: "600",
  },
  sectionHeading: {
    fontSize: "24px",
    fontWeight: "700",
    marginTop: "40px",
    marginBottom: "15px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "25px",
  },
  card: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    border: "1px solid #eee",
    transition: "all 0.35s ease",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  badge: {
    background: "#f44336",
    color: "#fff",
    fontSize: "12px",
    padding: "4px 8px",
    borderRadius: "20px",
    fontWeight: "700",
  },
  title: { marginTop: "15px", fontSize: "18px", fontWeight: "800" },
  meta: { fontSize: "14px", marginBottom: "8px", fontWeight: "600" },
  desc: { fontSize: "15px", minHeight: "45px", lineHeight: "1.6" },
  buttons: { marginTop: "15px", display: "flex", gap: "10px" },
  iconSpace: { marginRight: "6px" },
  viewBtn: {
    flex: 1,
    background: "#1976d2",
    color: "#fff",
    padding: "10px",
    borderRadius: "8px",
    border: "none",
    cursor: "pointer",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
  },
  downloadBtn: {
    flex: 1,
    textDecoration: "none",
    background: "#2e7d32",
    color: "#fff",
    padding: "10px",
    borderRadius: "8px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    width: "85%",
    height: "85%",
    background: "#fff",
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
  },
  modalContentFullscreen: {
    width: "100%",
    height: "100%",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    padding: "10px 15px",
    background: "#f5f5f5",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #ddd",
  },
  iconBtn: { background: "transparent", border: "none", cursor: "pointer" },
  iframe: { flex: 1, border: "none" },
};

export default Reports;